// src/services/ctiResultsService.js
import { api } from '../api/apiClient';

// REMOVED: getCTIResults, saveCTIResults, hasCTIResults — frontend uses per-attack-path CTIResult handlers

/**
 * Service for handling CTI results operations (per-attack-path)
 */
const ctiResultsService = {
  /**
   * Update CTI results for a specific analysis
   * @param {string} analysisId - Analysis ID
   * @param {Object} ctiData - CTI results data to update
   * @returns {Promise<Object>} Response data
   */
  updateCTIResults: async (analysisId, ctiData) => {
    try {
      const requestBody = {
        data: {
          ...ctiData,
          updatedAt: new Date().toISOString(),
          version: (ctiData.version || 1) + 1
        }
      };

      const response = await api.put(`/analyses/${analysisId}/cti-results`, requestBody);
      return response;
    } catch (error) {
      console.error('Error updating CTI results:', error);
      throw error;
    }
  },

  /**
   * Delete CTI results for a specific analysis
   * @param {string} analysisId - Analysis ID
   * @returns {Promise<Object>} Response data
   */
  deleteCTIResults: async (analysisId) => {
    try {
      const response = await api.delete(`/analyses/${analysisId}/cti-results`);
      return response;
    } catch (error) {
      if (error.response?.status === 404) {
        return { success: true, message: 'CTI results already deleted' };
      }
      console.error('Error deleting CTI results:', error);
      throw error;
    }
  },

  /**
   * Format CTI data for saving
   */
  formatCTIData: (attackPaths, vulnerabilities, techniques, metadata = {}) => {
    const attackPathsWithCTI = [];

    if (attackPaths && attackPaths.length > 0) {
      attackPaths.forEach(attackPath => {
        const pathVulnerabilities = vulnerabilities?.filter(v =>
          v.attackPathId === attackPath.id || v.attackPathId === attackPath.pathId
        ) || [];

        const pathTechniques = techniques?.filter(t =>
          t.attackPathId === attackPath.id || t.attackPathId === attackPath.pathId
        ) || [];

        if (pathVulnerabilities.length > 0 || pathTechniques.length > 0) {
          attackPathsWithCTI.push({
            id: attackPath.id || attackPath.pathId,
            pathId: attackPath.id || attackPath.pathId,
            pathName: attackPath.name || attackPath.pathName || attackPath.referenceCode || `Attack Path ${attackPath.id}`,
            referenceCode: attackPath.referenceCode,
            businessValueName: attackPath.businessValueName,
            sourceRiskName: attackPath.sourceRiskName,
            dreadedEventName: attackPath.dreadedEventName,
            objectifVise: attackPath.objectifVise,
            stakeholders: attackPath.stakeholders,
            description: attackPath.description,
            vulnerabilities: pathVulnerabilities,
            attackTechniques: pathTechniques,
            vulnerabilitiesCount: pathVulnerabilities.length,
            techniquesCount: pathTechniques.length,
            assetsCount: attackPath.assetsCount || 1
          });
        }
      });
    }

    return {
      attackPathsWithCTI: attackPathsWithCTI,
      totalAttackPaths: attackPaths?.length || 0,
      attackPathsWithCTICount: attackPathsWithCTI.length,
      summary: {
        criticalVulnerabilities: vulnerabilities?.filter(v => v.severity?.severity === 'CRITICAL')?.length || 0,
        highVulnerabilities: vulnerabilities?.filter(v => v.severity?.severity === 'HIGH')?.length || 0,
        mediumVulnerabilities: vulnerabilities?.filter(v => v.severity?.severity === 'MEDIUM')?.length || 0,
        lowVulnerabilities: vulnerabilities?.filter(v => v.severity?.severity === 'LOW')?.length || 0,
        mitreAttackTechniques: techniques?.filter(t => t.framework === 'MITRE ATT&CK' || t.source?.includes('MITRE'))?.length || 0,
        mitreAtlasTechniques: techniques?.filter(t => t.framework === 'MITRE ATLAS')?.length || 0
      },
      ...metadata
    };
  },

  /**
   * Extract business assets from attack paths
   */
  extractBusinessAssets: (attackPaths) => {
    const assets = new Set();
    attackPaths?.forEach(path => {
      if (path.businessValueName) {
        assets.add(path.businessValueName);
      }
    });
    return Array.from(assets);
  },

  /**
   * Get CTI results for a specific attack path
   */
  getCTIResultsForAttackPath: async (analysisId, attackPathId) => {
    try {
      const response = await api.get(`/analyses/${analysisId}/attack-paths/${attackPathId}/cti-results`);
      return response;
    } catch (error) {
      if (error.status === 404 || error.response?.status === 404) {
        return { data: null };
      }
      console.error('Error fetching CTI results for attack path:', error);
      throw error;
    }
  },

  /**
   * Save CTI results for a specific attack path
   */
  saveCTIResultsForAttackPath: async (analysisId, attackPathId, ctiData) => {
    try {
      const requestBody = {
        data: {
          ...ctiData,
          attackPathId: attackPathId,
          savedAt: new Date().toISOString(),
          version: ctiData.version || 1
        }
      };

      const response = await api.post(`/analyses/${analysisId}/attack-paths/${attackPathId}/cti-results`, requestBody);
      return response;
    } catch (error) {
      console.error('Error saving attack path CTI results:', error);
      throw error;
    }
  },

  /**
   * Delete CTI results for a specific attack path
   */
  deleteCTIResultsForAttackPath: async (analysisId, attackPathId) => {
    try {
      const response = await api.delete(`/analyses/${analysisId}/attack-paths/${attackPathId}/cti-results`);
      return response;
    } catch (error) {
      if (error.status === 404 || error.response?.status === 404) {
        return { success: true, message: 'Attack path CTI results already deleted' };
      }
      console.error('Error deleting attack path CTI results:', error);
      throw error;
    }
  },

  /**
   * Update the analyst note for a specific attack path's CTI results
   */
  updateCTINote: async (analysisId, attackPathId, note) => {
    try {
      const response = await api.patch(`/analyses/${analysisId}/attack-paths/${attackPathId}/cti-results`, { note });
      return response;
    } catch (error) {
      console.error('Error updating attack path CTI note:', error);
      throw error;
    }
  },

  /**
   * Check if CTI results exist for a specific attack path
   */
  hasCTIResultsForAttackPath: async (analysisId, attackPathId) => {
    try {
      const response = await ctiResultsService.getCTIResultsForAttackPath(analysisId, attackPathId);

      if (response && response.data) {
        const data = response.data.data || response.data;

        if (data && (data.vulnerabilities || data.techniques || data.assets)) {
          return {
            exists: true,
            createdAt: data.savedAt || data.metadata?.analysisDate || data.createdAt,
            updatedAt: data.updatedAt,
            vulnerabilitiesCount: data.vulnerabilities?.length || 0,
            techniquesCount: data.techniques?.length || 0,
            assetsCount: data.assets?.length || 0
          };
        }
      }
      return { exists: false };
    } catch (error) {
      return { exists: false };
    }
  },

  /**
   * Get CTI status for multiple attack paths
   */
  getCTIStatusForAttackPaths: async (analysisId, attackPathIds) => {
    try {
      const statusMap = {};
      const results = await Promise.allSettled(
        attackPathIds.map(pathId =>
          ctiResultsService.hasCTIResultsForAttackPath(analysisId, pathId)
        )
      );

      attackPathIds.forEach((pathId, index) => {
        const result = results[index];
        if (result.status === 'fulfilled') {
          statusMap[pathId] = result.value;
        } else {
          statusMap[pathId] = { exists: false };
        }
      });

      return statusMap;
    } catch (error) {
      const statusMap = {};
      attackPathIds.forEach(pathId => {
        statusMap[pathId] = { exists: false };
      });
      return statusMap;
    }
  },

  /**
   * Format CTI data for a specific attack path - PRESERVES ORIGINAL DATA STRUCTURE
   */
  formatCTIDataForAttackPath: (attackPath, vulnerabilities, techniques, assets, metadata = {}) => {
    return {
      attackPath: {
        id: attackPath.id,
        pathId: attackPath.id,
        pathName: attackPath.referenceCode || attackPath.objectifVise || `Attack Path ${attackPath.id}`,
        referenceCode: attackPath.referenceCode,
        businessValueName: attackPath.businessValueName,
        sourceRiskName: attackPath.sourceRiskName,
        dreadedEventName: attackPath.dreadedEventName,
        objectifVise: attackPath.objectifVise,
        stakeholders: attackPath.stakeholders,
        description: attackPath.description
      },
      vulnerabilities: vulnerabilities || [],
      techniques: techniques || [],
      assets: assets || [],
      metadata: {
        ...metadata,
        analysisDate: metadata.analysisDate || new Date().toISOString(),
        totalVulnerabilities: vulnerabilities?.length || 0,
        totalAttackTechniques: techniques?.length || 0,
        totalAssets: assets?.length || 0,
        attackPathId: attackPath.id
      }
    };
  },

  /**
   * Group CTI results by attack path
   */
  groupResultsByAttackPath: (ctiData) => {
    const grouped = {};
    ctiData.attackPaths?.forEach(path => {
      grouped[path.id] = {
        attackPath: path,
        vulnerabilities: ctiData.vulnerabilities?.filter(v =>
          v.attackPathId === path.id
        ) || [],
        techniques: ctiData.techniques?.filter(t =>
          t.attackPathId === path.id
        ) || []
      };
    });
    return grouped;
  },

  /**
   * Bulk load all CTI results for multiple attack paths using optimized backend endpoint
   */
  bulkLoadCTIResults: async (analysisId, attackPathIds) => {
    try {
      const response = await api.post(`/analyses/${analysisId}/cti-results/bulk`, {
        attackPathIds: attackPathIds
      });

      if (response?.success && response?.data) {
        const { ctiResults: rawResults, statusMap } = response.data;

        const ctiResultsMap = {};
        const timestampsMap = {};

        Object.keys(rawResults || {}).forEach(pathId => {
          const rawData = rawResults[pathId];
          if (rawData) {
            ctiResultsMap[pathId] = {
              attackPath: rawData.attackPath,
              assets: rawData.assets || [],
              analysisDate: rawData.metadata?.analysisDate || rawData.savedAt,
              overallRiskScore: rawData.metadata?.overallRiskScore,
              totalVulnerabilities: rawData.vulnerabilities?.length || 0,
              totalAttackTechniques: rawData.techniques?.length || 0,
              dataSource: rawData.metadata?.dataSource,
              vulnerabilities: rawData.vulnerabilities || [],
              techniques: rawData.techniques || [],
              itemStatuses: rawData.metadata?.itemStatuses || {}
            };

            if (rawData.metadata?.analysisDate || rawData.savedAt) {
              timestampsMap[pathId] = rawData.metadata?.analysisDate || rawData.savedAt;
            }
          }
        });

        return {
          ctiResults: ctiResultsMap,
          timestamps: timestampsMap,
          statusMap: statusMap || {}
        };
      }

      return { ctiResults: {}, timestamps: {}, statusMap: {} };

    } catch (error) {
      return await ctiResultsService.bulkLoadCTIResultsFallback(analysisId, attackPathIds);
    }
  },

  /**
   * Fallback method for bulk loading CTI results using individual requests
   */
  bulkLoadCTIResultsFallback: async (analysisId, attackPathIds) => {
    try {
      const results = await Promise.allSettled(
        attackPathIds.map(async (pathId) => {
          try {
            const response = await ctiResultsService.getCTIResultsForAttackPath(analysisId, pathId);
            const responseData = response?.data?.data || response?.data;

            if (responseData && (responseData.vulnerabilities || responseData.techniques || responseData.assets)) {
              return {
                pathId,
                success: true,
                data: {
                  attackPath: responseData.attackPath,
                  assets: responseData.assets || [],
                  analysisDate: responseData.metadata?.analysisDate,
                  overallRiskScore: responseData.metadata?.overallRiskScore,
                  totalVulnerabilities: responseData.vulnerabilities?.length || 0,
                  totalAttackTechniques: responseData.techniques?.length || 0,
                  dataSource: responseData.metadata?.dataSource,
                  vulnerabilities: responseData.vulnerabilities || [],
                  techniques: responseData.techniques || [],
                  itemStatuses: responseData.metadata?.itemStatuses || {}
                }
              };
            }
            return { pathId, success: false, data: null };
          } catch (error) {
            return { pathId, success: false, data: null };
          }
        })
      );

      const ctiResultsMap = {};
      const timestampsMap = {};

      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value.success) {
          const { pathId, data } = result.value;
          ctiResultsMap[pathId] = data;
          if (data.analysisDate) {
            timestampsMap[pathId] = data.analysisDate;
          }
        }
      });

      return { ctiResults: ctiResultsMap, timestamps: timestampsMap, statusMap: {} };

    } catch (error) {
      return { ctiResults: {}, timestamps: {}, statusMap: {} };
    }
  }
};

export default ctiResultsService;